import { useCallback, useRef, useState } from "react";

/** How long (ms) the finger must be held before drag-select mode activates. */
const TOUCH_LONG_PRESS_MS = 100;

/** Movement (px) that cancels long-press recognition before the timer fires (touch). */
const TOUCH_CANCEL_PX = 8;

/** Movement (px) that activates drag-select mode for mouse (avoids breaking plain clicks). */
const MOUSE_DRAG_PX = 6;

/**
 * Adds the "hold / drag → flyout → release to select" interaction to a
 * grouped `Tool` trigger, for both touch and mouse. Ports the original
 * BeltControls gesture verbatim — identical thresholds.
 *
 * Rows in the flyout opt in to drag-highlighting by marking themselves with
 * `data-belt-index="<n>"`; disabled rows additionally set `data-belt-disabled`.
 */
export function useToolGroupDrag({
  enabled,
  setOpen,
  onSelect,
}: {
  /** When false, pointer handlers become no-ops so singular Tools don't pay for this. */
  enabled: boolean;
  setOpen: (open: boolean) => void;
  onSelect: (index: number) => void;
}): {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  wrapOnClick: (handler: () => void) => () => void;
  dragHighlightIndex: number | null;
} {
  const [dragHighlightIndex, setDragHighlightIndex] = useState<number | null>(
    null,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const draggingRef = useRef(false);
  const suppressRef = useRef(false);

  const reset = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    originRef.current = null;
    draggingRef.current = false;
    setDragHighlightIndex(null);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      const isTouch = e.pointerType === "touch";
      const isMouse = e.pointerType === "mouse";
      if (!isTouch && !isMouse) return;
      originRef.current = { x: e.clientX, y: e.clientY };
      draggingRef.current = false;
      suppressRef.current = false;
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
      if (isTouch) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          draggingRef.current = true;
          suppressRef.current = true;
          setOpen(true);
        }, TOUCH_LONG_PRESS_MS);
      }
    },
    [enabled, setOpen],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      if (
        (e.pointerType !== "touch" && e.pointerType !== "mouse") ||
        !originRef.current
      )
        return;
      if (timerRef.current !== null) {
        const dx = e.clientX - originRef.current.x;
        const dy = e.clientY - originRef.current.y;
        if (dx * dx + dy * dy > TOUCH_CANCEL_PX * TOUCH_CANCEL_PX) reset();
        return;
      }
      if (e.pointerType === "mouse" && !draggingRef.current) {
        const dx = e.clientX - originRef.current.x;
        const dy = e.clientY - originRef.current.y;
        if (dx * dx + dy * dy > MOUSE_DRAG_PX * MOUSE_DRAG_PX) {
          draggingRef.current = true;
          suppressRef.current = true;
          setOpen(true);
        }
      }
      if (draggingRef.current) {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const item = el?.closest("[data-belt-index]") as HTMLElement | null;
        if (item && !item.hasAttribute("data-belt-disabled")) {
          setDragHighlightIndex(parseInt(item.dataset.beltIndex!, 10));
        } else {
          setDragHighlightIndex(null);
        }
      }
    },
    [enabled, reset, setOpen],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      if (e.pointerType !== "touch" && e.pointerType !== "mouse") return;
      if (draggingRef.current) {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const item = el?.closest("[data-belt-index]") as HTMLElement | null;
        if (item && !item.hasAttribute("data-belt-disabled")) {
          onSelect(parseInt(item.dataset.beltIndex!, 10));
        }
        setOpen(false);
      }
      reset();
    },
    [enabled, onSelect, reset, setOpen],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      if (e.pointerType !== "touch" && e.pointerType !== "mouse") return;
      reset();
      setOpen(false);
    },
    [enabled, reset, setOpen],
  );

  const wrapOnClick = useCallback(
    (handler: () => void) => () => {
      if (suppressRef.current) {
        suppressRef.current = false;
        return;
      }
      handler();
    },
    [],
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    wrapOnClick,
    dragHighlightIndex,
  };
}
